import {useCallback, useMemo, useRef, useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {CUSTOM_METADATA_COMPARISON_OPERATORS, QueryOperator, SizeSupportedOperators, VqlQuery} from "types";
import {convertAPIOperator} from "helpers/utility/queryFilterHelpers";
import {getSelectedQueryModal} from "redux/SingleDataset/selectors";
import {createAndUpdateSizeFilter, isVqlQuery,} from "helpers/utility/vqlHelpers";
import {setSelectedQueryModal} from "redux/SingleDataset/actions";
import useVqlParams from "hooks/useVqlParams";
import BaseQueryModal from "../CustomMetadataQueryModal/BaseQueryModal";
import QueryModalInput from "../../../../../components/QueryModal/QueryModalInput";
import ObjectSizeIcon from "assets/icons/ObjectSizeIcon";

interface LabelOutliersQueryModalProps {
    editQueryId?: string;
}

export default function ObjectSizeQueryModal({
                                                 editQueryId,
                                             }: LabelOutliersQueryModalProps) {
    const {findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries} =
        useVqlParams();
    const selectedModalData = useSelector((state) =>
        getSelectedQueryModal(state)
    );

    const issueQuery = editQueryId ? findVqlQuery(editQueryId, "size") : null;
    const oldQuery =
        issueQuery?.size.toString() ? issueQuery : null;

    const queryOperator = oldQuery
        ? convertAPIOperator(oldQuery.size.op)
        : QueryOperator.GREATER_THAN_OR_EQUAL_TO;

    const previousValue = (oldQuery?.size?.value);

    const [searchText, setSearchText] = useState<number | undefined>(previousValue);
    const [localOperator, setLocalOperator] = useState<QueryOperator>(queryOperator);

    useEffect(() => {
        setLocalOperator(queryOperator);
    }, [queryOperator]);

    const modalRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const existingQuery = useMemo(() => {
        if (editQueryId) {
            return null;
        }
        return (
            vqlQueries.find(
                (query: VqlQuery) =>
                    isVqlQuery(query, "size") &&
                    query.size &&
                    convertAPIOperator(query.size.op) === queryOperator
            ) || null
        );
    }, [vqlQueries, queryOperator, editQueryId]);

    const submitQuery = useCallback((
        operator: SizeSupportedOperators,
        newSize: number
    ) => {
        const queryId = editQueryId || existingQuery?.id;
        const vqlQuery = createAndUpdateSizeFilter(
            newSize,
            operator,
            queryId
        );
        queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
        if (selectedModalData) {
            dispatch(
                setSelectedQueryModal({...selectedModalData, id: vqlQuery.id})
            );
        }
    }, [
        editQueryId,
        existingQuery?.id,
        selectedModalData,
        dispatch,
        updateVqlQuery,
        addVqlQuery,
    ]);

    const handleOptionSelection = (operator: QueryOperator) => {
        setLocalOperator(operator);
    };

    const handleSubmit = () => {
        if (searchText !== undefined) {
            submitQuery(localOperator as SizeSupportedOperators, searchText);
            dispatch(setSelectedQueryModal(null));
        }
    };

    const handleInputChange = (value?: string) => {
        if (value === "") {
            setSearchText(undefined);
        } else {
            setSearchText(Number(value));
        }
    };

    return <BaseQueryModal<QueryOperator>
        modalRef={modalRef}
        keepOpenOnLoading
        operators={{
            options: [...CUSTOM_METADATA_COMPARISON_OPERATORS],
            selected: localOperator as SizeSupportedOperators,
            onSelect: handleOptionSelection,
        }}
        onSubmit={handleSubmit}
        isSubmitDisabled={!searchText || (searchText === previousValue && localOperator === queryOperator)}
        title={"Object Size"}
        icon={<ObjectSizeIcon/>}
    >
        <QueryModalInput
            numberOnly
            searchText={searchText?.toString() || ""}
            placeholder="e.g 500PX"
            onInputChange={handleInputChange}
        />
    </BaseQueryModal>
}
